// Shared layout maths for the schedule's dense views.
//
// Ported from Zanetto Tools (Schedule.jsx packJobLanes / packLanes), never
// forked — the reasoning comes across as prose.
import type { DateOnly } from '../../lib/dates';
import type { JobDayGroup } from '../../lib/jobGroups';

/**
 * A stable vertical order for the week's job tiles.
 *
 * Each job gets ONE lane index for the whole week, so a job renders in the
 * same relative position every day it appears — the eye tracks "the Fenwick
 * St job" across the row instead of re-finding it in every column. Ranking:
 * day-count DESCENDING (a job on site five days sits above one there for
 * two), then earliest appearance, then job number for stability. Lanes are
 * not reserved as blanks — a job absent on Wednesday just doesn't render
 * there — but relative order is preserved (ZT 490-509).
 */
export function packJobLanes(
  groupsByDay: Map<DateOnly, JobDayGroup[]>
): Map<string, number> {
  const stats = new Map<string, { days: number; first: DateOnly; subtitle: string }>();

  for (const [date, groups] of groupsByDay) {
    for (const group of groups) {
      const entry = stats.get(group.key);
      if (entry) {
        entry.days += 1;
        if (date < entry.first) entry.first = date;
      } else {
        stats.set(group.key, { days: 1, first: date, subtitle: group.subtitle ?? '' });
      }
    }
  }

  const ordered = [...stats.entries()].sort(([, a], [, b]) => {
    if (a.days !== b.days) return b.days - a.days;
    if (a.first !== b.first) return a.first.localeCompare(b.first);
    return a.subtitle.localeCompare(b.subtitle);
  });

  return new Map(ordered.map(([key], index) => [key, index]));
}
